import React, { useMemo, useState, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { MRT_ColumnDef, MRT_Row, MRT_TableOptions, useMaterialReactTable, MRT_SortingState } from 'material-react-table';
import { UserPermissionDto } from 'src/generated/services/TFinancialApi';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { openToast } from 'src/use-cases/toast/useToast';
import { Box, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, FormHelperText, Chip } from '@mui/material';
import { useUpsertUserPermission, useDeleteUserPermission } from 'src/use-cases/settings/useUsers';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';

interface UserPermissionTableProps {
  permissions: UserPermissionDto[];
  userId?: string;
}

export const UserPermissionTable = forwardRef<any, UserPermissionTableProps>(({ permissions, userId }, ref) => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<UserPermissionDto | null>(null);

  // Keep permissions stable during editing and mutations
  const stablePermissions = useMemo(() => {
    // If we're currently mutating, maintain the current permissions to prevent disruption
    if (isMutating) {
      return permissions;
    }
    return permissions;
  }, [permissions, isMutating]);

  // Default sort by ID in ascending order
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'id',
      desc: false,
    },
  ]);
  const { mutateAsync: upsertPermission } = useUpsertUserPermission();
  const { mutateAsync: deletePermission, isLoading: isDeleting } = useDeleteUserPermission();

  // Helper function to clear validation errors
  const clearValidationError = useCallback((fieldName: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  }, []);

  // Validate permission fields
  const onValidatePermission = useCallback((permission: Record<string, any>) => {
    // No required fields, return empty validation result
    const validationResult = {};

    return validationResult;
  }, []);

  // CREATE action
  const handleCreatePermission: MRT_TableOptions<UserPermissionDto>['onCreatingRowSave'] = ({ values, exitCreatingMode }) => {
    // Apply default values to the new row data
    const rowData: Partial<UserPermissionDto> = {
      // Set default values directly here
      userId: userId || '',
      permissions: [],
      allowed: false,
      // Override with any values from the form
      ...values,
    };

    const newValidationErrors = onValidatePermission(rowData);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    if (!userId) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'User ID is required',
      });
      return;
    }

    // Helper function to convert empty strings to null
    const emptyToNull = (value: string | null | undefined): string | null => (value === '' ? null : value || null);

    const reqBody: UserPermissionDto = {
      userId,
      billedEntityCode: emptyToNull(rowData.billedEntityCode),
      shipperCode: emptyToNull(rowData.shipperCode),
      consigneeCode: emptyToNull(rowData.consigneeCode),
      serviceProviderCode: emptyToNull(rowData.serviceProviderCode),
      shippingServiceId: emptyToNull(rowData.shippingServiceId),
      allowed: rowData.allowed,
      permissions: rowData.permissions || [],
    };

    // Handle the async operation without making the function async
    setIsMutating(true);
    upsertPermission(reqBody)
      .then(() => {
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Permission created successfully!',
        });
        // Exit creating mode immediately to prevent state conflicts
        exitCreatingMode();
        // Keep mutating state for a longer period to prevent cache invalidation disruption
        setTimeout(() => {
          setIsMutating(false);
        }, 1200); // Slightly longer than the cache invalidation delay
      })
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        openToast({
          toastType: 'error',
          title: 'Error',
          description: errorMessage,
        });
        // Still exit creating mode on error to prevent stuck state
        exitCreatingMode();
        setIsMutating(false);
      });
  };

  // UPDATE action
  const handleUpdatePermission: MRT_TableOptions<UserPermissionDto>['onEditingRowSave'] = ({ row, values, exitEditingMode }) => {
    const newValidationErrors = onValidatePermission(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    const reqBody: UserPermissionDto = {
      id: row.original.id,
      userId: row.original.userId,
      billedEntityCode: values.billedEntityCode,
      shipperCode: values.shipperCode,
      consigneeCode: values.consigneeCode,
      serviceProviderCode: values.serviceProviderCode,
      shippingServiceId: values.shippingServiceId,
      allowed: values.allowed,
      permissions: values.permissions || [],
    };

    // Handle the async operation without making the function async
    setIsMutating(true);
    setEditingRowId(row.id);

    upsertPermission(reqBody)
      .then(() => {
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Permission updated successfully!',
        });
        // Exit edit mode immediately to prevent state conflicts
        exitEditingMode();
        // Keep mutating state for a longer period to prevent cache invalidation disruption
        setTimeout(() => {
          setIsMutating(false);
          setEditingRowId(null);
        }, 1200); // Slightly longer than the cache invalidation delay
      })
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        openToast({
          toastType: 'error',
          title: 'Error',
          description: errorMessage,
        });
        // Still exit edit mode on error to prevent stuck state
        exitEditingMode();
        setIsMutating(false);
        setEditingRowId(null);
      });
  };

  // DELETE action
  const handleDeletePermission = async (permission: UserPermissionDto) => {
    if (!permission.id) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Cannot delete permission: missing ID',
      });
      return;
    }

    try {
      await deletePermission({ id: permission.id.toString() });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Permission deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  // Define the renderRowActions function separately with proper typing
  const renderRowActions = useCallback((props: { row: MRT_Row<UserPermissionDto>; table: any }) => {
    // Explicitly extract the props to satisfy ESLint
    const { row, table } = props;

    // Use table in a way that ESLint can detect
    const editingRow = () => {
      if (table && typeof table.setEditingRow === 'function') {
        table.setEditingRow(row);
      }
    };

    return (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={editingRow}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, []);

  // Define permission options with useMemo to avoid recreating on every render
  const permissionOptions = useMemo(
    () => [
      { label: 'AUDIT', value: 'AUDIT' },
      { label: 'PROCUREMENT', value: 'PROCUREMENT' },
      { label: 'DISPUTE', value: 'DISPUTE' },
      { label: 'FINANCE', value: 'FINANCE' },
      { label: 'RATECARD', value: 'RATECARD' },
      { label: 'OPERATION', value: 'OPERATION' },
      { label: 'PERFORMANCE', value: 'PERFORMANCE' },
      { label: 'REPORT', value: 'REPORT' },
    ],
    []
  );

  // Define allowed options with useMemo to avoid recreating on every render
  const allowedOptions = useMemo(
    () => [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
    []
  );

  // Default values are now handled directly in the handleCreatePermission function

  // Define columns
  const columns = useMemo<MRT_ColumnDef<UserPermissionDto>[]>(
    () => [
      {
        accessorKey: 'billedEntityCode',
        header: 'Billed Entity',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.billedEntityCode,
          helperText: validationErrors?.billedEntityCode,
          onFocus: () => {
            if (validationErrors?.billedEntityCode) {
              clearValidationError('billedEntityCode');
            }
          },
        },
      },
      {
        accessorKey: 'shipperCode',
        header: 'Shipper',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.shipperCode,
          helperText: validationErrors?.shipperCode,
          onFocus: () => {
            if (validationErrors?.shipperCode) {
              clearValidationError('shipperCode');
            }
          },
        },
      },
      {
        accessorKey: 'consigneeCode',
        header: 'Consignee',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.consigneeCode,
          helperText: validationErrors?.consigneeCode,
          onFocus: () => {
            if (validationErrors?.consigneeCode) {
              clearValidationError('consigneeCode');
            }
          },
        },
      },
      {
        accessorKey: 'serviceProviderCode',
        header: 'Service Provider',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.serviceProviderCode,
          helperText: validationErrors?.serviceProviderCode,
          onFocus: () => {
            if (validationErrors?.serviceProviderCode) {
              clearValidationError('serviceProviderCode');
            }
          },
        },
      },
      {
        accessorKey: 'shippingServiceId',
        header: 'Shipping Service',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.shippingServiceId,
          helperText: validationErrors?.shippingServiceId,
          onFocus: () => {
            if (validationErrors?.shippingServiceId) {
              clearValidationError('shippingServiceId');
            }
          },
        },
      },
      {
        accessorKey: 'allowed',
        header: 'Allowed',
        editVariant: 'select',
        editSelectOptions: allowedOptions,
        Cell: ({ cell }) => {
          const value = cell.getValue() as boolean;
          return value === true ? 'Yes' : 'No';
        },
        muiEditTextFieldProps: {
          select: true,
          required: false,
          error: !!validationErrors?.allowed,
          helperText: validationErrors?.allowed,
          onFocus: () => {
            if (validationErrors?.allowed) {
              clearValidationError('allowed');
            }
          },
        },
      },
      {
        accessorKey: 'permissions',
        header: 'Permissions',
        Cell: ({ cell }) => {
          const permissions = cell.getValue();
          return Array.isArray(permissions) && permissions.length > 0 ? permissions.join(', ') : '-';
        },
        Edit: ({ column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;

          // Get current value or default to empty array
          const currentValue = row.original.permissions || [];

          return (
            <FormControl fullWidth error={!!validationErrors?.permissions} sx={{ minWidth: 120 }} required={false}>
              <InputLabel id='permissions-select-label'>Permissions</InputLabel>
              <Select
                labelId='permissions-select-label'
                multiple
                defaultValue={currentValue}
                onChange={({ target: { value } }) => {
                  // Update the row cache with the new value
                  // eslint-disable-next-line no-underscore-dangle
                  row._valuesCache[column.id] = value;

                  // Update the row in the table state
                  if (isCreating) {
                    table.setCreatingRow(row);
                  } else if (isEditing) {
                    table.setEditingRow(row);
                  }

                  // Clear validation error if present
                  if (validationErrors?.permissions) {
                    clearValidationError('permissions');
                  }
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size='small' />
                    ))}
                  </Box>
                )}
              >
                {permissionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors?.permissions && <FormHelperText>{validationErrors.permissions}</FormHelperText>}
            </FormControl>
          );
        },
      },
    ],
    [validationErrors, allowedOptions, permissionOptions]
  );

  // Create table instance with stable configuration
  const table = useMaterialReactTable({
    columns,
    data: stablePermissions,
    enableEditing: true,
    enableRowActions: true,
    positionActionsColumn: 'first',
    getRowId: (row, index) => {
      // Always use the permission ID if available for existing permissions
      if (row.id !== undefined && row.id !== null) {
        return `permission-${row.id}`;
      }
      // For new rows without IDs, create a stable ID based on userId and row content hash
      const contentHash = JSON.stringify({
        billedEntityCode: row.billedEntityCode,
        shipperCode: row.shipperCode,
        consigneeCode: row.consigneeCode,
        serviceProviderCode: row.serviceProviderCode,
        shippingServiceId: row.shippingServiceId,
      });
      return `new-${userId}-${index}-${btoa(contentHash).slice(0, 8)}`;
    },
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreatePermission,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleUpdatePermission,
    defaultColumn: {
      minSize: 50,
      maxSize: 300,
      size: 150,
    },
    // We'll handle default values in the onCreatingRowSave callback instead
    state: {
      sorting,
      isLoading: isMutating, // Show loading state during mutations
    },
    onSortingChange: setSorting,
    muiTableContainerProps: {
      sx: {
        minHeight: '200px',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
      },
    },
    // Explicitly set display modes for creating and editing
    editDisplayMode: 'row',
    createDisplayMode: 'row',
    // Enable toolbar to show create button
    enableTopToolbar: true,
    // Ensure the table renders the toolbar properly
    renderTopToolbarCustomActions: ({ table }) => (
      <div className='flex justify-end w-full'>
        <button onClick={() => table.setCreatingRow(true)} className='px-4 py-2 bg-green-600 text-white rounded-md text-sm'>
          Add New Permission
        </button>
      </div>
    ),
    renderEmptyRowsFallback: () => (
      <div className='flex justify-center items-center p-16'>
        <p className='text-neutral-60'>No permissions found</p>
      </div>
    ),
    renderRowActions,
  });

  // Expose table methods to parent component
  useImperativeHandle(ref, () => ({
    setCreatingRow: (creating: boolean) => {
      if (creating) {
        // For MRT V3, just use true to start creating a row
        // We'll handle default values in the onCreatingRowSave callback
        table.setCreatingRow(true);
      } else {
        table.setCreatingRow(null);
      }
    },
  }));

  return (
    <>
      <SettingsTableMain table={table} />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeletePermission(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this user permission?</p>
      </ModalConfirm>
    </>
  );
});
